"use client";

import { useTranslations } from "next-intl";
import { Crown } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/shared/ui/data-display/avatar";
import type { LfgLftRosterSlot } from "@/shared/api/lfg-lft-types";
import { cn } from "@/shared/lib/utils";
import { isLfgRole, toLfgRank } from "../model/board-filters";
import { CardChip, LfgRoleIcon, RankLabel } from "./board-card-parts";

const initials = (name: string) => name.slice(0, 2).toUpperCase();

/** Состав команды строками (макет): кто есть, кого ищут, что свободно. */
export function BoardTeamRoster({ slots }: { slots: LfgLftRosterSlot[] }) {
  const t = useTranslations("LfgBoard");
  const filled = slots.filter((s) => s.slotStatus === "Filled").length;

  return (
    <section className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between text-xs">
        <h3 className="font-bold uppercase tracking-[1.2px] text-muted-foreground">{t("card.roster")}</h3>
        <span className="font-semibold text-highlight">{t("card.rosterCount", { filled, total: slots.length })}</span>
      </div>
      <ul className="flex flex-col gap-1.5">
        {slots.map((slot) => {
          const role = isLfgRole(slot.lane) ? slot.lane : null;
          const roleName = role ? t(`role.${role}`) : slot.lane;

          if (slot.slotStatus !== "Filled")
            return (
              <li
                key={slot.lane}
                className={cn(
                  "flex items-center gap-2.5 rounded-[10px] border border-dashed px-2.5 py-2",
                  slot.slotStatus === "Seeking" ? "border-highlight-border bg-highlight-soft/50" : "border-border",
                )}
              >
                <span
                  className={cn(
                    "grid size-7 flex-none place-items-center rounded-full",
                    slot.slotStatus === "Seeking" ? "bg-highlight-soft text-highlight" : "bg-muted text-muted-foreground",
                  )}
                >
                  {role && <LfgRoleIcon role={role} />}
                </span>
                <span className={cn("flex-1 text-[13px] font-bold", slot.slotStatus === "Seeking" ? "text-highlight" : "text-muted-foreground")}>
                  {roleName}
                </span>
                <CardChip className={slot.slotStatus === "Seeking" ? "text-highlight" : "text-muted-foreground"}>
                  {slot.slotStatus === "Seeking" ? t("card.seeking") : t("card.free")}
                </CardChip>
              </li>
            );

          const name = slot.displayName ?? "—";
          return (
            <li key={slot.lane} className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5 rounded-[10px] border border-border bg-muted/30 px-2.5 py-2">
              {/* Узко — чипы ранга и роли переносятся под ник, а не обрезают его до «El Shpi…». */}
              <div className="flex min-w-0 flex-[1_1_9rem] items-center gap-2.5">
                <Avatar className="size-7 flex-none">
                  <AvatarImage src={slot.avatarUrl ?? undefined} alt="" />
                  <AvatarFallback className="font-logo text-[11px]">{initials(name)}</AvatarFallback>
                </Avatar>
                {slot.isCaptain && <Crown aria-label={t("card.captain")} className="size-3.5 flex-none text-highlight" />}
                <span className={cn("truncate text-[13px] font-bold", slot.isCaptain && "text-highlight")}>{name}</span>
              </div>
              <div className="ml-auto flex flex-none flex-wrap justify-end gap-1.5">
                <CardChip>
                  <RankLabel rank={toLfgRank(slot.rank)} />
                </CardChip>
                {role && (
                  <CardChip>
                    <LfgRoleIcon role={role} />
                    {roleName}
                  </CardChip>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
